import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, Loader2, Star } from 'lucide-react';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ReviewReminder() {
  const [order, setOrder] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('ww-last-order');
      if (raw) setOrder(JSON.parse(raw));
    } catch (_e) {
      // ignore — malformed localStorage data should not crash the success page (see /app/CODE_REVIEW_NOTES.md §5)
    }
  }, []);

  if (!order || !order.items?.length) return null;

  // Dedupe by product id (no point showing same bracelet twice)
  const seen = new Set();
  const items = order.items.filter((it) => {
    if (seen.has(it.product_id)) return false;
    seen.add(it.product_id);
    return true;
  });

  return (
    <div
      className="bg-[#354F52] text-[#FAF9F6] p-7 rounded-sm mb-8 text-left"
      data-testid="review-reminder"
    >
      <div className="flex items-center gap-3 mb-3">
        <Star className="w-5 h-5 text-[#D4AF37] fill-[#D4AF37]" strokeWidth={1.5} />
        <h2 className="font-serif text-lg">Once it arrives, share your story</h2>
      </div>
      <p className="text-sm text-[#CAD2C5] leading-relaxed mb-5">
        Your bracelet ships within 3-5 days. When it lands on your wrist, we&apos;d love your honest review — it helps Lindy make every next bracelet a little more thoughtful.
      </p>
      <div className="space-y-3">
        {items.map((it) => (
          <div
            key={it.product_id}
            className="flex items-center gap-4 bg-[#52796F]/40 rounded-sm p-3"
            data-testid={`reminder-item-${it.product_id}`}
          >
            {it.image && (
              <img
                src={it.image}
                alt={it.product_name}
                className="w-14 h-14 object-cover rounded-sm shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-serif text-sm text-[#FAF9F6] truncate">
                {it.product_name}
              </p>
              <p className="text-xs text-[#CAD2C5] mt-0.5">
                We&apos;ll remember you 💚
              </p>
            </div>
            <Link
              to={`/products/${it.product_id}`}
              className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#FAF9F6] transition-colors whitespace-nowrap"
              data-testid={`leave-review-link-${it.product_id}`}
            >
              Leave review →
            </Link>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#84A98C] mt-4 italic">
        Tip: bookmark this page or save the link — come back once your bracelet has arrived.
      </p>
    </div>
  );
}

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentMethod = searchParams.get('method');
  const orderIdFromUrl = searchParams.get('order_id');
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    // If coming from PayPal, confirm the order server-side so receipts go out.
    if (paymentMethod === 'paypal') {
      // Read order_id either from the return URL or from the last-order snapshot.
      let orderId = orderIdFromUrl;
      if (!orderId) {
        try {
          const raw = localStorage.getItem('ww-last-order');
          if (raw) orderId = JSON.parse(raw)?.order_id || null;
        } catch (_e) { /* ignore */ }
      }
      if (orderId) {
        axios
          .post(`${API}/orders/${orderId}/confirm`)
          .then(({ data }) => setConfirmedOrder(data))
          .catch((err) => logger.error('Order confirm failed:', err));
      }
      setStatus('success');
      clearCart();
      return;
    }

    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          setPaymentInfo(response.data);

          if (response.data.payment_status === 'paid') {
            setStatus('success');
            clearCart(); // Clear cart on successful payment
            return;
          } else if (response.data.status === 'expired') {
            setStatus('expired');
            return;
          }

          // Continue polling if payment is still pending
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            // After max attempts, show what we have
            if (response.data.payment_status === 'paid') {
              setStatus('success');
              clearCart();
            } else {
              setStatus('pending');
            }
          }
        } catch (error) {
          logger.error('Error checking payment status:', error);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            setStatus('error');
          }
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [sessionId, paymentMethod, orderIdFromUrl, clearCart]);

  if (status === 'loading') {
    return (
      <div className="pt-28 pb-16 min-h-screen" data-testid="checkout-loading">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="max-w-lg mx-auto text-center">
            <Loader2 className="w-16 h-16 text-[#D4AF37] animate-spin mx-auto mb-8" strokeWidth={1.5} />
            <h1 className="heading-display text-2xl text-[#354F52] mb-4">
              Verifying Payment...
            </h1>
            <p className="text-[#52796F]">
              Please wait while we confirm your payment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="pt-28 pb-16 min-h-screen" data-testid="checkout-success">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-[#52796F] rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-[#FAF9F6]" strokeWidth={1.5} />
            </div>
            
            <h1 className="heading-display text-3xl md:text-4xl text-[#354F52] mb-4">
              Thank You!
            </h1>
            
            <p className="text-[#52796F] mb-8">
              Your order has been confirmed. We&apos;re already starting on your
              handcrafted Morse code bracelet.
            </p>

            <div className="bg-[#F5F5F0] p-8 rounded-sm mb-8 text-left">
              <div className="flex items-center gap-3 mb-4">
                <Package className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                <h2 className="font-serif text-lg text-[#354F52]">Order Details</h2>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#84A98C]">Order Status</span>
                  <span className="text-[#52796F] font-medium">Confirmed</span>
                </div>
                {confirmedOrder?.order_id && (
                  <div className="flex justify-between" data-testid="order-id-row">
                    <span className="text-[#84A98C]">Order Reference</span>
                    <span className="text-[#52796F] font-mono text-xs">
                      #{confirmedOrder.order_id.slice(0, 8).toUpperCase()}
                    </span>
                  </div>
                )}
                {confirmedOrder?.total != null ? (
                  <div className="flex justify-between">
                    <span className="text-[#84A98C]">Amount Paid</span>
                    <span className="text-[#D4AF37] font-medium">
                      €{confirmedOrder.total.toFixed(2)}
                    </span>
                  </div>
                ) : paymentInfo && (
                  <div className="flex justify-between">
                    <span className="text-[#84A98C]">Amount Paid</span>
                    <span className="text-[#D4AF37] font-medium">
                      €{(paymentInfo.amount_total / 100).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#84A98C]">Estimated Delivery</span>
                  <span className="text-[#52796F]">3-5 business days</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-[#84A98C] mb-8">
              A confirmation email is on its way to your inbox with your full order details.
            </p>

            {/* Review reminder */}
            <ReviewReminder />

            <Link
              to="/products"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="continue-shopping-success"
            >
              Continue Shopping
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="pt-28 pb-16 min-h-screen" data-testid="checkout-expired">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="max-w-lg mx-auto text-center">
            <h1 className="heading-display text-2xl text-[#354F52] mb-4">
              Session Expired
            </h1>
            <p className="text-[#52796F] mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/cart" className="btn-primary">
              Return to Cart
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="pt-28 pb-16 min-h-screen" data-testid="checkout-pending">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="max-w-lg mx-auto text-center">
            <h1 className="heading-display text-2xl text-[#354F52] mb-4">
              Payment Processing
            </h1>
            <p className="text-[#52796F] mb-8">
              Your payment is being processed. You&apos;ll receive a confirmation email shortly.
            </p>
            <Link to="/products" className="btn-primary">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="pt-28 pb-16 min-h-screen" data-testid="checkout-error">
      <div className="container-custom px-6 md:px-12 lg:px-24">
        <div className="max-w-lg mx-auto text-center">
          <h1 className="heading-display text-2xl text-[#354F52] mb-4">
            Something Went Wrong
          </h1>
          <p className="text-[#52796F] mb-8">
            We couldn&apos;t verify your payment. Please contact support if you were charged.
          </p>
          <Link to="/cart" className="btn-primary">
            Return to Cart
          </Link>
        </div>
      </div>
    </div>
  );
}
